// photoCache — persistent local cache for family photos, keyed by
// SHA-256 content hash.
//
// Why hash-keyed? FamTree photos live in the private `family-photos`
// bucket. Reading them requires a signed URL that expires (1h), so
// keying a cache by URL is useless — the URL changes every session.
// Hashing the bytes gives a stable, content-addressed key that the
// backend returns directly (family_photos.hash, migration 05002).
// The client checks this store first and only downloads objects
// whose hash is missing (the user's requested flow: "client should
// just request the image that is not in local persistent storage").
//
// Storage: IndexedDB when available (persistent across sessions,
// survives page reloads), an in-memory Map otherwise (jsdom tests,
// private browsing, or browsers without indexedDB). The public API
// is identical in both modes so callers don't branch on support.
//
// The DB name/version pair is deliberately namespaced per app so a
// future "clear cache" feature can wipe only family photos.

const DB_NAME = "famchest-photos";
const DB_VERSION = 1;
const STORE = "photos";

let memoryFallback: Map<string, Blob> | null = null;

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined" && !!indexedDB;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE); // key = hash
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
      tx.onerror = () => reject(tx.error ?? new Error("indexedDB tx failed"));
    });
  } finally {
    db.close();
  }
}

/** SHA-256 hex digest of a Blob. Uses crypto.subtle when available
 *  (secure contexts); falls back to a fast non-crypto hash of the
 *  first 256 KiB + size + mtime so non-secure LAN deployments still
 *  get deterministic, content-derived keys (not security — cache
 *  keys only, collision risk negligible for photos). */
export async function sha256Hex(blob: Blob, mtimeMs?: number): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const buf = await blob.arrayBuffer();
    const digest = await subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Non-secure fallback: FNV-1a over head bytes + size + mtime.
  const head = blob.slice(0, 256 * 1024);
  const bytes = new Uint8Array(await head.arrayBuffer());
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193);
  }
  const meta = `${bytes.length}:${blob.size}:${mtimeMs ?? 0}`;
  let m = 0;
  for (let i = 0; i < meta.length; i++) {
    m = (Math.imul(m, 31) + meta.charCodeAt(i)) | 0;
  }
  return `fnv-${(h >>> 0).toString(16)}-${(m >>> 0).toString(16)}`;
}

/** Resolve one photo blob from the local cache. */
export async function getCachedPhoto(hash: string): Promise<Blob | null> {
  if (!idbAvailable()) {
    return memoryFallback?.get(hash) ?? null;
  }
  try {
    return (await withStore("readonly", (s) => s.get(hash))) as Blob | null;
  } catch {
    return null; // cache unreadable → treat as miss, not a crash
  }
}

/** Persist one photo blob under its hash. */
export async function putCachedPhoto(hash: string, blob: Blob): Promise<void> {
  if (!idbAvailable()) {
    (memoryFallback ??= new Map()).set(hash, blob);
    return;
  }
  try {
    await withStore("readwrite", (s) => s.put(blob, hash));
  } catch {
    // Cache write failure is non-fatal — the image still renders
    // from the network this session.
  }
}

/** Remove one photo blob from the local cache (used on delete). */
export async function deleteCachedPhoto(hash: string): Promise<void> {
  if (!idbAvailable()) {
    memoryFallback?.delete(hash);
    return;
  }
  try {
    await withStore("readwrite", (s) => s.delete(hash));
  } catch {
    // best-effort
  }
}

/** Which of the given hashes are NOT in the local cache. */
export async function missingHashes(hashes: readonly string[]): Promise<string[]> {
  const unique = Array.from(new Set(hashes));
  if (unique.length === 0) return [];
  const missing: string[] = [];
  await Promise.all(
    unique.map(async (h) => {
      if (!(await getCachedPhoto(h))) missing.push(h);
    }),
  );
  return missing;
}
